import React from "react"
import { Amount, Promo } from "@ssc-booking/api-contracts"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as EQ from "fp-ts/Eq"
import * as ORD from "fp-ts/Ord"
import * as ROA from "fp-ts/ReadonlyArray"
import * as RONEA from "fp-ts/ReadonlyNonEmptyArray"
import * as SG from "fp-ts/Semigroup"
import * as Semigroup from "fp-ts/Semigroup"
import * as S from "fp-ts/string"
import * as Apply from "fp-ts/Apply"
import { sequenceT } from "fp-ts/Apply"
import { i18n } from "@lingui/core"
import { t, Trans } from "@lingui/macro"
import * as N from "fp-ts/number"
import { formatAmount } from "../../../app-core/_common/format/localization"
import { ReceiptRow, ReceiptRowBullet, ReceiptSection } from "../types"

export const included = O.some(Amount.zero("USD"))
export const emptyRows = F.constant([] as readonly ReceiptSection[])
export const isDiscount = (amount: O.Option<Amount> | Amount): boolean => {
    if ("currency" in amount) return Amount.isZeroOrNegative(amount)
    return O.isSome(amount) && Amount.isZeroOrNegative(amount.value)
}

export const formatPromotionalAmount = F.flow(
    O.map((a: Amount) => (Amount.isZero(a) ? i18n._(t`price-included`) : formatAmount(a))),
    O.getOrElse(() => ""),
)

export const removeRowsIfAllHaveSameAmount = <T extends ReceiptSection | ReceiptRowBullet>(section: T): T => ({
    ...section,
    rows: allRowsHaveSameAmount(section) ? [] : section.rows,
})

const allRowsHaveSameAmount = <T extends ReceiptSection | ReceiptRowBullet>(section: T) => {
    const uniqueRowsByAmount = F.pipe(section.rows, ROA.uniq(byAmount))
    return uniqueRowsByAmount.length === 1
}

const byAmount = F.pipe(
    O.getEq(Amount.eq),
    EQ.contramap((x: ReceiptRow) => x.amount),
)

export const groupPriceSummariesByKey = (
    a: RONEA.ReadonlyNonEmptyArray<ReceiptSection>,
): RONEA.ReadonlyNonEmptyArray<ReceiptSection> =>
    F.pipe(
        a,
        RONEA.sort(byType),
        RONEA.group<ReceiptSection>(byType),
        RONEA.map(RONEA.concatAll(semigroupPriceSummarySection)),
    )

const byType: ORD.Ord<ReceiptSection> = F.pipe(
    S.Ord,
    ORD.contramap((s) => s.key),
)

const semigroupPriceSummarySection: SG.Semigroup<ReceiptSection> = {
    concat: (a, b) => ({
        ...a,
        isPromotional: a.isPromotional && b.isPromotional,
        totalAmount: maybeSumAmount(a.totalAmount, b.totalAmount),
        rows: [...a.rows, ...b.rows],
    }),
}

const maybeSumAmount = (first: O.Option<Amount>, second: O.Option<Amount>): O.Option<Amount> =>
    F.pipe(
        sequenceTO(first, second),
        O.map(([a, b]) => Amount.sum(a, b)),
    )

const sequenceTO = sequenceT(O.Apply)

export const groupPriceSummaryRowDataByLabel = (
    a: RONEA.ReadonlyNonEmptyArray<ReceiptRowBullet>,
): RONEA.ReadonlyNonEmptyArray<ReceiptRowBullet> =>
    F.pipe(
        a,
        RONEA.sort(byLabel),
        RONEA.group<ReceiptRowBullet>(byLabel),
        RONEA.map(RONEA.concatAll(semigroupPriceSummaryRowData)),
    )

const byLabel: ORD.Ord<ReceiptRowBullet> = F.pipe(
    S.Ord,
    ORD.contramap((promo) => promo.label),
)

const semigroupPriceSummaryRowData: SG.Semigroup<ReceiptRowBullet> = {
    concat: (a, b) => ({
        ...a,
        amount: OptionalAmountSemigroup.concat(a.amount, b.amount),
        isPromotional: a.isPromotional && b.isPromotional,
        rows: [...(a.rows || []), ...(b.rows || [])],
    }),
}

export const guestLabel = (number: string | number) => t`Guest #${number}`

export const gatewayLabel = (gateway: string) => (
    <React.Fragment>
        <Trans>gateway</Trans>: <strong>{gateway}</strong>
    </React.Fragment>
)

export const vsCodeLabel = (vsCode: string) => (
    <React.Fragment>
        <Trans>Membership number</Trans>: <strong>{vsCode}</strong>
    </React.Fragment>
)

const OptionSemigroupOf = Apply.getApplySemigroup(O.Apply)
const OptionalAmountSemigroup = OptionSemigroupOf(Amount.SemigroupSum)
const EqualNumbersSemigroup: Semigroup.Semigroup<O.Option<number>> = {
    concat: (a: O.Option<number>, b: O.Option<number>) => {
        return O.isSome(a) && O.isSome(b) && a.value === b.value ? a : O.none
    },
}

export const PriceDetailPromoSemigroup = Semigroup.struct({
    promo: Semigroup.first<Promo>(),
    configurationCode: OptionSemigroupOf(Semigroup.first<string>()),
    discount: Amount.SemigroupSum,
    credit: OptionalAmountSemigroup,
    percentage: EqualNumbersSemigroup,
})

export const PriceDetailPromoEqByValues = EQ.struct({
    discount: Amount.eq,
    credit: O.getEq(Amount.eq),
    percentage: O.getEq(N.Eq),
})
